import React, { useState, useEffect } from 'react';
import './CookieBanner.css';

const STORAGE_KEY = 'recodeq_cookie_consent';
const CONSENT_VERSION = 1;
const EXPIRY_DAYS = 365;

const CookieBanner = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  
  const [preferences, setPreferences] = useState({
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    // 1. Ošetření výchozího stavu pro Google Analytics (pokud jej používáte)
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      window.gtag('consent', 'default', {
        ad_storage: 'denied',
        ad_user_data: 'denied',
        ad_personalization: 'denied',
        analytics_storage: 'denied',
        wait_for_update: 500,
      });
    }

    // 2. Načtení dat z localStorage
    const raw = localStorage.getItem(STORAGE_KEY);
    let existingConsent = null;

    if (raw) {
      try {
        const data = JSON.parse(raw);
        // Kontrola, zda neexpirovala platnost nebo nebyla změněna verze souhlasu
        if (data.version === CONSENT_VERSION && Date.now() <= data.expires) {
          existingConsent = data;
        }
      } catch (e) {
        console.error("Chyba při čtení cookies", e);
      }
    }

    if (existingConsent) {
      applyConsent(existingConsent);
      setPreferences({
        analytics: !!existingConsent.analytics,
        marketing: !!existingConsent.marketing,
      });
    } else {
      // Pokud nemáme souhlas, zobrazíme lištu
      setIsVisible(true);
    }

    // Globální funkce (aby šlo např. z patičky zavolat otevření nastavení - window.openCookieSettings())
    window.openCookieSettings = () => {
      setIsVisible(true);
      setShowSettings(true);
    };
  }, []);

  const applyConsent = (data) => {
    // Aplikace do GTAG
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      window.gtag('consent', 'update', {
        ad_storage: data.marketing ? 'granted' : 'denied',
        ad_user_data: data.marketing ? 'granted' : 'denied',
        ad_personalization: data.marketing ? 'granted' : 'denied',
        analytics_storage: data.analytics ? 'granted' : 'denied',
      });
    }
    // Odeslání custom eventu do aplikace
    window.dispatchEvent(new CustomEvent('cookieConsentUpdate', { detail: data }));
  };

  const saveConsent = (analytics, marketing) => {
    const data = {
      version: CONSENT_VERSION,
      analytics: analytics,
      marketing: marketing,
      timestamp: Date.now(),
      expires: Date.now() + EXPIRY_DAYS * 24 * 60 * 60 * 1000,
    };
    
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    applyConsent(data);
    
    setPreferences({ analytics, marketing });
    setIsVisible(false);
    setShowSettings(false);
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setPreferences((prev) => ({ ...prev, [name]: checked }));
  };

  // Pokud je lišta skrytá, zobrazíme pouze to plovoucí tlačítko 🍪 vlevo dole
  if (!isVisible) {
    return (
      <button
        type="button"
        className="cookie-reopen"
        aria-label="Nastavení cookies"
        onClick={() => setIsVisible(true)}
      >
        🍪
      </button>
    );
  }

  return (
    <div className="cookie-banner" role="dialog" aria-live="polite" aria-label="Souhlas s cookies">
      <div className="cookie-banner__inner">
        <div className="cookie-banner__text">
          <h2>Používáme cookies 🍪</h2>
          <p>
            Na webu RECODEQ používáme cookies, abychom zajistili správné fungování webu, 
            analyzovali návštěvnost a zlepšovali naše služby. Kliknutím na „Přijmout vše" souhlasíte 
            s jejich používáním. Souhlas můžete kdykoli upravit. Více v <a href="/zasady-cookies">Zásadách zpracování osobních údajů a cookies</a>.
          </p>
        </div>
        <div className="cookie-banner__actions">
          <button 
            type="button" 
            className="cookie-btn cookie-btn--primary" 
            onClick={() => saveConsent(false, false)}
          >
            Odmítnout vše
          </button>
          <button 
            type="button" 
            className="cookie-btn cookie-btn--secondary" 
            onClick={() => setShowSettings(!showSettings)}
          >
            Nastavení
          </button>
          <button 
            type="button" 
            className="cookie-btn cookie-btn--primary" 
            onClick={() => saveConsent(true, true)}
          >
            Přijmout vše
          </button>
        </div>
      </div>

      {showSettings && (
        <div className="cookie-settings">
          <h3>Nastavení cookies</h3>
          <label className="cookie-option">
            <input type="checkbox" checked disabled />
            <div>
              <strong>Nezbytné</strong>
              <p>Nutné pro základní fungování webu. Nelze vypnout.</p>
            </div>
          </label>
          <label className="cookie-option">
            <input 
              type="checkbox" 
              name="analytics" 
              checked={preferences.analytics}
              onChange={handleCheckboxChange}
            />
            <div>
              <strong>Analytické</strong>
              <p>Pomáhají nám rozumět, jak návštěvníci web používají (Google Analytics 4).</p>
            </div>
          </label>
          <label className="cookie-option">
            <input 
              type="checkbox" 
              name="marketing" 
              checked={preferences.marketing}
              onChange={handleCheckboxChange}
            />
            <div>
              <strong>Marketingové</strong>
              <p>Slouží k cílení reklamy a měření její účinnosti.</p>
            </div>
          </label>
          <div className="cookie-banner__actions" style={{ marginTop: '16px' }}>
            <button 
              type="button" 
              className="cookie-btn cookie-btn--primary" 
              onClick={() => saveConsent(preferences.analytics, preferences.marketing)}
            >
              Uložit nastavení
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CookieBanner;